<template>
	<PanelDraggable :data="data" :draggable="isDraggable">
		<CallDraggable :data="employees.me" :type="CallDragTargetType.MyCalls" :target="true" :draggable="false" :targetTypes="[CallDragItemType.Employee, CallDragItemType.QueueCall]">

			<!-- HEADER -->
			<Button v-if="dashboards.editMode" size="small" severity="danger" class="dash-delete-btn" @click="remove()">
				<GlyphIcon :icon="GlyphIconName['bin']" color="#fff" size="18" />
			</Button>
			<div class="dash-header" @mouseover="mouseOverHeader(true)" @mouseout="mouseOverHeader(false)" @click="headerClicked" :editMode="dashboards.editMode" :class="{'missed-calls': callHistory.missedCallsCount > 0}">
				<GlyphIcon :icon="GlyphIconName['call']" color="#fff" size="18" style="margin-top:2px" />
				<div class="dotdotdot" style="margin-left:3px">{{ t('mycalls.header') }} <span v-if="employees.myCalls.length > settings.maxCalls">({{ employees.myCalls.length }})</span></div>
				<div v-show="!dashboards.editMode" class="flex flex-row items-start gap-1.5 justify-between">
					<div v-show="employees.me?.primaryExtension?.personalQueue" class="personal-queue-calls" v-tooltip.top="{ value: t('mycalls.personalQueueTooltip'), showDelay: 1000, hideDelay: 300 }">
						<GlyphIcon :icon="GlyphIconName['user-group']" size="17" style="margin-top:2px" />
						<div>{{ employees.me?.primaryExtension?.personalQueue }}</div>
					</div>
					<Button size="small" variant="text" class="header-button" @click.stop="toggleRecentCalls()" v-tooltip.top="{ value: t('mycalls.recentCalls'), showDelay: 1000, hideDelay: 300 }">
						<GlyphIcon :icon="showRecentCalls ? GlyphIconName['chevron-up'] : GlyphIconName['history']" size="19" />
					</Button>
					<Button size="small" variant="text" class="header-button" @click.stop="toggleDTMF()" v-if="ss.settings.softphoneId" v-tooltip.top="{ value: t('mycalls.dtmf'), showDelay: 1000, hideDelay: 300 }">
						<GlyphIcon :icon="showDTMF ? GlyphIconName['chevron-up'] : GlyphIconName['table']" size="19" />
					</Button>
				</div>
			</div>

			<!-- EDIT MODE -->
			<div v-if="dashboards.editMode" class="dash-settings-box">
				<div class="flex items-center gap-4">
					<div class="w-full mb-3">
						<div>{{ t('mycalls.maxShownCalls')  }}&nbsp;<span class="text-muted text-sm">({{ t('mycalls.maxShownCallsDescription') }})</span></div>
						<Select v-model="settings.maxCalls" fluid :options="maxCallsOptions" optionValue="value" optionLabel="value" size="small" append-to="self" />
					</div>
				</div>
				<div class="w-full flex items-center gap-4">
					<div class="w-1/2">
						<div>{{ t('mycalls.callVariableName') }}</div>
						<InputText size="small" maxlength="64" style="width:100%" v-model="settings.variable" @keypress="inputValidator($event, /[a-zA-Z0-9-_]/)" @paste="inputPasteValidator($event, /^[a-zA-Z0-9-_]+$/)" />
					</div>
					<div class="w-1/2">
						<div>{{ t('mycalls.callVariableLabel') }}</div>
						<InputText size="small" maxlength="32" style="width:100%" v-model="settings.variableName" />
					</div>
				</div>
				<div class="text-muted text-sm">{{ t('mycalls.callVariableDescription') }}</div>
			</div>

			<!-- VIEW MODE -->
			<div v-if="!dashboards.editMode&&employees.me" class="dtmf-box" :class="{ 'show' : showDTMF }">
				<div v-for="key in ['1','2','3','4','5','6','7','8','9','0','*','#']" @click="dtmf(key)" :key="key" class="dtmf-button">{{ key }}</div>
			</div>

			<div v-if="!dashboards.editMode&&employees.me" class="recent-calls-box" :class="{ 'show' : showRecentCalls }">
				<div class="flex justify-center" style="background:var(--p-togglebutton-background);border-bottom: 1px solid var(--p-select-border-color);">
					<SelectButton size="small" v-model="recentCategory" :options="recentCategories" optionValue="value" optionLabel="label" :allowEmpty="false" />
				</div>
				<div v-for="(call,index) in recentCallsList" :key="index" class="dotdotdot recent-call-item" @click="dial(call)" @contextmenu.prevent="showRecentCallListMenu(call, $event)">
					<div v-if="recentCategory === 'outbound'"><GlyphIcon :icon="callHistoryIcon(call)" :color="callHistoryIconColor(call)" size="22" /></div>
					<div v-if="recentCategory === 'inbound'"><GlyphIcon :icon="callHistoryIcon(call)" :color="callHistoryIconColor(call)" size="22" /></div>
					<div v-if="recentCategory === 'missed'"><GlyphIcon :icon="GlyphIconName['square-alert']" color="var(--cdr-busy)" size="22" /></div>

					<div class="time" v-if="call.started.toISOString().substring(0,10) === new Date().toISOString().substring(0,10)">{{ t('common.today') }} {{ formatDate(call.started, 'HH:mm') }}</div>
					<div class="time" v-if="call.started.toISOString().substring(0,10) !== new Date().toISOString().substring(0,10)">{{ formatDate(call.started, 'v HH:mm') }}</div>

					<div v-if="recentCategory === 'outbound' && call.callee.name" class="name dotdotdot">{{ call.callee.name }}</div>
					<div v-if="recentCategory === 'outbound'" class="number" :class="{'noname': !call.callee.name}">{{ ss.cutNationalPrefix(call.callee.number) }}</div>

					<div v-if="recentCategory !== 'outbound' && call.caller.name" class="name dotdotdot">{{ call.caller.name }}</div>
					<div v-if="recentCategory !== 'outbound' && !call.caller.number && !call.caller.name" class="name">{{ t('mycalls.hiddenNumber') }}</div>
					<div v-if="recentCategory !== 'outbound'" class="number" :class="{'noname': !call.caller.name}">{{ ss.cutNationalPrefix(call.caller.number) }}</div>
				</div>
				<div v-if="recentCallsList.length === 0" style="color: var(--p-text-color)" class="w-full text-center">{{ t('mycalls.noCallsFound') }}</div>

			</div>
			<ContextMenu ref="recentCallsListMenu" :model="recentCallsListMenuItems" />

			<div v-if="!dashboards.editMode && employees.me" class="view-box" :style="{'height': ((data.maxCalls * 51) + 15) + 'px'}">
				<transition-group name="list" tag="div">
					<div v-for="(call, index) in employees.myCalls" :key="call._id">
						<MyCallsCall :index="index" :variable="data.variable" :variableName="data.variableName" />
					</div>
				</transition-group>
			</div>

			<div style="position: relative;height:37px" v-if="ss.settings.softphoneId || ss.settings.phoneId">
				<div class="bottom-buttons" :class="{ show: employees.myCalls.length > 0 }">
					<div class="flex justify-center gap-3 opacity-80 p-2">
						<Button size="small" severity="primary"
							raised rounded class="flex-1 min-w-0" style="height:24px"
							@click="runBottomAction('answer', bottomAnswer)"
							v-show="canAnswer" :disabled="(busyAction === 'answer') || (actionsBusy && busyAction !== 'answer')"><i class="p-button-icon" :class="(busyAction === 'answer') ? 'pi pi-spinner pi-spin' : 'pi pi-phone'" /><span>{{ t('mycalls.action.answer') }}</span></Button>
						<Button size="small" severity="danger"
							raised rounded class="flex-1 min-w-0" style="height:24px"
							@click="runBottomAction('reject', bottomReject)"
							v-show="ringingCall" :disabled="(busyAction === 'reject') || (actionsBusy && busyAction !== 'reject')"><i class="p-button-icon" :class="(busyAction === 'reject') ? 'pi pi-spinner pi-spin' : 'pi pi-ban'" /><span>{{ t('mycalls.action.reject') }}</span></Button>
						<Button size="small" severity="danger"
							raised rounded class="flex-1 min-w-0" style="height:24px"
							@click="runBottomAction('hangup', bottomHangup)"
							v-show="(activeCall || heldCall || dialingCall) && !ringingCall" :disabled="(busyAction === 'hangup') || (actionsBusy && busyAction !== 'hangup')"><i class="p-button-icon" :class="(busyAction === 'hangup') ? 'pi pi-spinner pi-spin' : 'pi pi-phone rotate-135'" /><span>{{ t('mycalls.action.hangup') }}</span></Button>
						<Button size="small" severity="secondary"
							raised rounded class="flex-1 min-w-0" style="height:24px"
							@click="runBottomAction('toggleHold', bottomToggleHold)"
							v-show="(activeCall || (heldCall && heldCall.iOwnHold)) && !twoCalls" :disabled="(busyAction === 'toggleHold') || (actionsBusy && busyAction !== 'toggleHold')"><i class="p-button-icon" :class="(busyAction === 'toggleHold') ? 'pi pi-spinner pi-spin' : 'pi pi-pause'" /><span>{{ !twoCalls && heldCall ? t('mycalls.action.resume') : t('mycalls.action.hold') }}</span></Button>
						<!-- Not one of the locked bottom actions: turning the camera on takes seconds, and must not keep hang up waiting -->
						<Button v-if="showCamera" size="small" :severity="cameraOn ? 'secondary' : 'primary'"
							raised rounded class="shrink-0" style="height:24px;width:40px"
							:aria-label="cameraLabel" v-tooltip.top="{ value: cameraLabel, showDelay: 1000, hideDelay: 300 }"
							:disabled="cameraStarting || cameraUnavailable" @click="toggleActiveCallCamera()"><i v-if="cameraStarting" class="p-button-icon pi pi-spinner pi-spin" /><GlyphIcon v-else :icon="cameraOn ? GlyphIconName['video-off'] : GlyphIconName['video']" size="16" /></Button>
						<Button size="small" severity="warn"
							raised rounded class="flex-1 min-w-0" style="height:24px"
							@click="runBottomAction('swap', bottomSwap)"
							v-show="twoCalls && activeCall && heldCall && !activeIsGuest" :disabled="(busyAction === 'swap') || (actionsBusy && busyAction !== 'swap')"><i class="p-button-icon" :class="(busyAction === 'swap') ? 'pi pi-spinner pi-spin' : 'pi pi-sync'" /><span>{{ t('mycalls.action.swap') }}</span></Button>
						<Button size="small" severity="success"
							raised rounded class="flex-1 min-w-0" style="height:24px"
							@click="runBottomAction('transfer', bottomTransfer)"
							v-show="twoCalls && activeCall && heldCall && !activeIsGuest" :disabled="(busyAction === 'transfer') || (actionsBusy && busyAction !== 'transfer')"><i class="p-button-icon" :class="(busyAction === 'transfer') ? 'pi pi-spinner pi-spin' : 'pi pi-reply'" /><span>{{ t('mycalls.action.transfer') }}</span></Button>
						<Button size="small" severity="success"
							raised rounded class="flex-1 min-w-0" style="height:24px"
							@click="runBottomAction('transferBlind', bottomTransferToDialing)"
							v-show="twoCalls && dialingCall && heldCall && !activeIsGuest" :disabled="(busyAction === 'transferBlind') || (actionsBusy && busyAction !== 'transferBlind')"><i class="p-button-icon" :class="(busyAction === 'transferBlind') ? 'pi pi-spinner pi-spin' : 'pi pi-reply'" /><span>{{ t('mycalls.action.transferBlind') }}</span></Button>
					</div>
				</div>
			</div>
		</CallDraggable>
	</PanelDraggable>
</template>

<script setup lang="ts">
import { useSettingsStore } from '@/stores/Settings'
import { useCallHistoryStore } from '@/stores/CallHistory'
import { useDashboardsStore } from '@/stores/Dashboards'
import { useEmployeesStore } from '@/stores/Employees'
import { useApiStore } from '@/stores/Api'
import { useMyCallsStore } from '@/stores/MyCalls'
import { useI18n } from 'vue-i18n'
import globalEvents from '@/utilities/GlobalEvents'
import SipClient from '@/utilities/SipClient'
import logger from '@/utilities/Logger'
import GlyphIcon from '@/components/GlyphIcon.vue'
import PanelDraggable from '@/components/dashboard/PanelDraggable.vue'
import MyCallsCall from '@/components/dashboard/MyCallsCall.vue'
import CallDraggable from '@/components/dashboard/CallDraggable.vue'
import { type PbxAppDashboardItemMyCalls, type PbxAppEmployeesExtensionCallWithDate } from '@/TcxApi/PbxApp'
import { computed, ref, watch } from 'vue'
import { PbxCdrTerminationReason, type PbxCdrLatestCallRecord } from '@/TcxApi/PbxCdr'
import { CallDragItemType, CallDragTargetType } from '@/utilities/CallDrag'
import { GlyphIconName } from '@/stores/GlyphIcons.types'
import { formatDate } from '@/utilities/filters'
import type ContextMenu from 'primevue/contextmenu'
import { PbxPhonebookContactPhoneNumberType, PbxPhonebookContactType, type PbxPhonebookContact } from '@/TcxApi/PbxPhonebook'
import { DK_NUMBER_TYPE, dkNumberType, sleep } from '@/utilities/Tools'
import type { MenuItem } from 'primevue/menuitem'
import { PbxCallState } from '@/TcxApi/PbxCommon'
import { isGuestCall } from '@/utilities/guestCallActions'
import { showCameraButton, toggleCamera, videoStateFor } from '@/utilities/video/visibility'
import { cameraInUse } from '@/stores/VideoCalls'

const ss = useSettingsStore()
const callHistory = useCallHistoryStore()
const dashboards = useDashboardsStore()
const employees = useEmployeesStore()
const api = useApiStore()
const myCalls = useMyCallsStore()
const { t } = useI18n()
const sipClient = SipClient.instance

interface Props {
	data: PbxAppDashboardItemMyCalls
}

const props = defineProps<Props>()
const settings = dashboards.dashboard?.items.find(item => item === props.data) as PbxAppDashboardItemMyCalls

const isDraggable = ref(false)
const showDTMF = ref(false)
const showRecentCalls = ref(false)
const recentCategory = ref<'outbound' | 'inbound' | 'missed'>('outbound')
const recentCategories = ref([
	{ value: 'outbound', label: t('mycalls.recent.outbound') },
	{ value: 'inbound', label: t('mycalls.recent.inbound') },
	{ value: 'missed', label: t('mycalls.recent.missed') }
])
const dialInitiatedAt = ref(new Date())

const maxCallsOptions = [
	{ value: 1 },
	{ value: 2 },
	{ value: 3 },
	{ value: 4 },
	{ value: 5 },
	{ value: 6 },
	{ value: 7 },
	{ value: 8 },
	{ value: 9 },
	{ value: 10 },
	{ value: 15 },
	{ value: 20 },
	{ value: 25 },
	{ value: 30 }
]

const recentCallsList = computed(() => { return callHistory[recentCategory.value as keyof typeof callHistory] as PbxCdrLatestCallRecord[] })
const recentCallsListMenu = ref<InstanceType<typeof ContextMenu> | undefined>(undefined)
const recentCallsListSelected = ref<PbxCdrLatestCallRecord | undefined>(undefined)

const canSendSms = computed(() => {
	if (recentCallsListSelected.value) {
		const number = recentCategory.value === 'outbound' ? recentCallsListSelected.value.callee.number : recentCallsListSelected.value.caller.number

		return dkNumberType(number) === DK_NUMBER_TYPE.MOBILE
	} else {
		return false
	}
})

const dialingCall = computed<PbxAppEmployeesExtensionCallWithDate | undefined>(() => { return employees.myCalls.find(call => call.state === PbxCallState.CALL_START) })
const ringingCall = computed<PbxAppEmployeesExtensionCallWithDate | undefined>(() => { return employees.myCalls.find(call => call.state === PbxCallState.CALL_RING) })
const activeCall = computed<PbxAppEmployeesExtensionCallWithDate | undefined>(() => { return employees.myCalls.find(call => call.state === PbxCallState.CALL_ANSWER) })
const heldCall = computed<PbxAppEmployeesExtensionCallWithDate | undefined>(() => { return employees.myCalls.find(call => call.state === PbxCallState.CALL_HOLD) })
/** On a guest call there is no transfer and no swap - the PBX refuses them */
const activeIsGuest = computed(() => isGuestCall(activeCall.value) || isGuestCall(heldCall.value) || isGuestCall(dialingCall.value))
const twoCalls = computed(() => { return employees.myCalls.length === 2 })

// The answer button only works for a softphone or SIP phone. A call ringing on a mobile (or external)
// phone can only be answered on the device itself, so the button must not be shown for those phone types.
const canAnswer = computed(() => ringingCall.value && ['SIPPHONE', 'SOFTPHONE'].includes(myCalls.phoneType))

// The camera of the active call - see utilities/video/visibility for when it can be used. With no active call, that of
// a held call whose camera is still on, so it can be turned off: the other party may hold the call with my camera on.
const cameraCall = computed(() => {
	if (activeCall.value) { return activeCall.value }
	return heldCall.value && cameraInUse(videoStateFor(heldCall.value)) ? heldCall.value : undefined
})
const showCamera = computed(() => !!cameraCall.value && showCameraButton(cameraCall.value))
const cameraCallVideo = computed(() => (cameraCall.value ? videoStateFor(cameraCall.value) : undefined))
const cameraOn = computed(() => cameraCallVideo.value?.local === 'on')
const cameraStarting = computed(() => cameraCallVideo.value?.local === 'starting')
const cameraUnavailable = computed(() => !cameraOn.value && !!cameraCallVideo.value?.unavailable) // rejected earlier in the call: shown, disabled
const cameraLabel = computed(() => {
	if (cameraOn.value) { return t('mycalls.videoTurnOffCamera') }
	return cameraUnavailable.value ? t('mycalls.videoNotAvailable') : t('mycalls.videoTurnOnCamera')
})

function toggleActiveCallCamera() {
	if (cameraCall.value) { toggleCamera(cameraCall.value, 'MyCalls') }
}

const recentCallsListMenuItems: MenuItem[] = [
	{ label: t('common.call'), icon: 'pi pi-phone', command: () => dial(recentCallsListSelected.value as PbxCdrLatestCallRecord) },
	{ label: t('mycalls.action.sendSms'), icon: 'pi pi-comment', command: () => sendSms(recentCallsListSelected.value as PbxCdrLatestCallRecord), visible: () => canSendSms.value },
	{ label: t('mycalls.action.copyNumberToClipboard'), icon: 'pi pi-copy', command: () => copyNumberToClipboard(recentCallsListSelected.value as PbxCdrLatestCallRecord) },
	{ label: t('mycalls.action.createContact'), icon: 'pi pi-user-plus', command: () => createContactFromRecentCall(recentCallsListSelected.value as PbxCdrLatestCallRecord) }
]

function callHistoryIconColor(call: PbxCdrLatestCallRecord) {
	switch (call.terminatedReason) {
		case PbxCdrTerminationReason.BLOCKED: return 'var(--cdr-error)'
		case PbxCdrTerminationReason.UNREACHABLE: return 'var(--cdr-error)'
		case PbxCdrTerminationReason.BUSY: return 'var(--cdr-busy)'
		case PbxCdrTerminationReason.CANCEL: return 'var(--cdr-cancelled)'
		case PbxCdrTerminationReason.ERROR: return 'var(--cdr-error)'
		case PbxCdrTerminationReason.HANGUP: return 'var(--cdr-success)'
		case PbxCdrTerminationReason.NOT_FOUND: return 'var(--cdr-error)'
		default: return 'var(--cdr-success)'
	}
}

function callHistoryIcon(call: PbxCdrLatestCallRecord) {
	switch (call.terminatedReason) {
		case PbxCdrTerminationReason.BLOCKED: return GlyphIconName['bug']
		case PbxCdrTerminationReason.UNREACHABLE: return GlyphIconName['mobile-phone-off']
		case PbxCdrTerminationReason.BUSY: return GlyphIconName['user-no']
		case PbxCdrTerminationReason.CANCEL: return GlyphIconName['cloud']
		case PbxCdrTerminationReason.ERROR: return GlyphIconName['triangle-empty-alert']
		case PbxCdrTerminationReason.HANGUP: return GlyphIconName['check']
		case PbxCdrTerminationReason.NOT_FOUND: return GlyphIconName['circle-empty-question']
		default: return GlyphIconName['check']
	}
	// GlyphIconName['circle-right']
}

function sendSms(call: PbxCdrLatestCallRecord) {
	const number = recentCategory.value === 'outbound' ? call.callee.number : call.caller.number

	globalEvents.emit('CommonSendSmsModal', { employee: undefined, number: ss.cutNationalPrefix(number) })
}

function showRecentCallListMenu(call: PbxCdrLatestCallRecord, event: MouseEvent) {
	recentCallsListSelected.value = call
	recentCallsListMenu.value?.show(event)
}

function createContactFromRecentCall(call: PbxCdrLatestCallRecord) {
	const item = recentCategory.value === 'outbound' ? call.callee : call.caller

	if (item.number) {
		const contact: PbxPhonebookContact = {
			_id: '',
			customer: ss.auth?.customer as string,
			type: PbxPhonebookContactType.PERSON,
			addresses: [],
			phoneNumbers: [{ type: PbxPhonebookContactPhoneNumberType.MAIN, number: item.number }],
			emailAddresses: [],
			notes: '',
			employee: ss.auth?.employee as string,
			givenName: null,
			familyName: null,
			company: null,
			title: null,
			department: null,
			shortNumber: null,
			picture: null,
			externalId: null,
			name: null
		}
		if (item.name && item.name.includes(' ')) {
			contact.givenName = item.name.substring(0, item.name.lastIndexOf(' '))
			contact.familyName = item.name.substring(item.name.lastIndexOf(' ') + 1)
		} else if (item.name) {
			contact.givenName = item.name
		}

		globalEvents.emit('Phonebook:CreateNew', contact)
	}
}

function dtmf(key: string) {
	globalEvents.emit('MyCallsSendDTMF', key)
	logger.info(`MyCalls: User pressed DTMF TONE ${key} on screen`)
}

function toggleDTMF() {
	showDTMF.value = !showDTMF.value
	logger.info(`MyCalls: User toggled visibility of DTMF panel - it is now ${showDTMF.value ? 'visible' : 'hidden'}`)
}

function remove() {
	dashboards.removeItem(props.data.column, props.data.position)
}

function mouseOverHeader(itIs: boolean) {
	if (itIs && dashboards.editMode && !dashboards.dragItem) {
		isDraggable.value = true
	} else {
		isDraggable.value = false
	}
}

function headerClicked() {
	if (!dashboards.editMode && callHistory.missedCallsCount > 0) {
		ss.cdrModalVisible = !ss.cdrModalVisible
	}
}

async function toggleRecentCalls() {
	showRecentCalls.value = !showRecentCalls.value
	logger.info(`MyCalls: User toggles visibility of recent calls - it is now ${showRecentCalls.value ? 'visible' : 'hidden'}`)
}

function copyNumberToClipboard(call: PbxCdrLatestCallRecord) {
	const number = ss.cutNationalPrefix(recentCategory.value === 'outbound' ? call.callee.number : call.caller.number) as string

	navigator.clipboard.writeText(number)
	logger.info(`MyCalls: User copied number ${number} to clipboard`)
}

async function dial(call: PbxCdrLatestCallRecord) {
	if (new Date().getTime() - dialInitiatedAt.value?.getTime() < 2000) { return }
	dialInitiatedAt.value = new Date()
	const direction = call.caller.extension == employees.me?.primaryExtension?._id ? 'OUT' : 'IN'
	const other = direction === 'IN' ? call.caller : call.callee
	if (!ss.realPhoneId) {
		globalEvents.emit('CommonErrorModal', { header: t('common.error.noPhoneHeader'), message: t('common.error.noPhoneMessage') })
		return
	}

	if (ss.settings.softphoneId) {
		const target = ss.cutNationalPrefix(other.number)
		if (target && /^\+?\d+$/.test(target)) {
			showRecentCalls.value = false
			logger.info(`MyCalls: User calling ${target} from recent calls`)
			sipClient.call(target)
		} else {
			globalEvents.emit('CommonErrorModal', { header: t('mycalls.cannotDialHiddenHeader'), message: t('mycalls.cannotDialHiddenMessage') })
		}
	} else {
		const target = other.extension ? other.extension : ss.cutNationalPrefix(other.number)
		if (target && (/^\+?\d+$/.test(target) || /^[a-fA-F0-9]{24}$/.test(target))) {
			showRecentCalls.value = false
			logger.info(`MyCalls: User calling ${target} from recent calls`)

			try { await api.get(`/pbx/app/action/call?device=${ss.settings.phoneId}&target=${encodeURIComponent(target)}`) }
			catch (_) { globalEvents.emit('CommonErrorModal', { header: t('common.error.callFailedHeader'), message: t('common.error.callFailedMessage') }) }
		} else {
			globalEvents.emit('CommonErrorModal', { header: t('mycalls.cannotDialHiddenHeader'), message: t('mycalls.cannotDialHiddenMessage') })
		}
	}
}

async function initWatchers() {
	await employees.ready()
	if (employees.me) {
		watch(() => employees.myCalls, (newValue: PbxAppEmployeesExtensionCallWithDate[]) => {
			if (newValue.length === 0 && showDTMF.value) { showDTMF.value = false }
		})

		watch(() => ss.runtime.selectedSection, (value: string) => {
			if (!value.startsWith('DASHBOARD')) {
				showDTMF.value = false
				showRecentCalls.value = false
			} else {
				callHistory.missedCallsCount = 0
			}
		})
	}
}

function inputValidator(event: KeyboardEvent, rx: RegExp) {
	if (event.key.length === 1 && !rx.test(event.key)) {
		event.preventDefault()
	}
}

function inputPasteValidator(event: ClipboardEvent, rx: RegExp) {
	const clipboardData = event.clipboardData?.getData('text/plain')
	if (clipboardData && !rx.test(clipboardData)) {
		event.preventDefault()
	}
}

// #region Bottom buttons

// Locks all bottom action buttons while an action runs to prevent double-clicking
// and rapid repeated clicking (e.g. swapping between two calls over and over).
const actionsBusy = ref(false)
const busyAction = ref<string | null>(null)

async function runBottomAction(name: string, action: () => void | Promise<void>) {
	if (actionsBusy.value) { return }
	actionsBusy.value = true
	busyAction.value = name

	try {
		await action()
	} catch (err) {
		logger.error(`MyCalls: Error occurred while running bottom action '${name}': ${(err as Error).message}`)
	} finally {
		// Keep buttons locked for 1 second after the action completes.
		await sleep(1000)
		actionsBusy.value = false
		busyAction.value = null
	}
}

function bottomAnswer() {
	if (ringingCall.value && ss.settings.softphoneId) {
		const session = sipClient.getSession(ringingCall.value.callId, ringingCall.value.caller.number, ringingCall.value.state)
		if (session) {
			globalEvents.emit('Call:Answer', { sessionId: session.id })
		}
	} else if (ringingCall.value && ss.settings.phoneId) {
		try { api.get(`/pbx/app/action/answer?device=${ss.realPhoneId}&target=${ringingCall.value._id}`) }
		catch (_) { globalEvents.emit('CommonErrorModal', { header: t('mycalls.header'), message: t('mycalls.failedToAnswerCall') }) }
	}
}

function bottomReject() {
	if (ringingCall.value && ss.settings.softphoneId) {
		const session = sipClient.getSession(ringingCall.value.callId, ringingCall.value.caller.number, ringingCall.value.state)
		if (session) {
			globalEvents.emit('Call:Reject', { sessionId: session.id })
		}
	} else if (ringingCall.value && ss.settings.phoneId) {
		if (ringingCall.value.state === PbxCallState.CALL_RING) {
			globalEvents.emit('CallRejected', { source: ringingCall.value._id })
		}

		try { api.get(`/pbx/app/action/hangup?target=${ringingCall.value._id}`) }
		catch (_) {	globalEvents.emit('CommonErrorModal', { header: t('mycalls.header'), message: t('mycalls.failedToHangupCall') }) }
	}
}

function bottomHangup() {
	if (ss.settings.softphoneId) {
		if (dialingCall.value && heldCall.value) {
			const session = sipClient.getSession(dialingCall.value.callId, dialingCall.value.caller.number, dialingCall.value.state)
			if (session) {
				globalEvents.emit('Call:Hangup', { sessionId: session.id })
			}
		} else if (activeCall.value) {
			const session = sipClient.getSession(activeCall.value.callId, activeCall.value.caller.number, activeCall.value.state)
			if (session) {
				globalEvents.emit('Call:Hangup', { sessionId: session.id })
			}
		} else if (heldCall.value) {
			const session = sipClient.getSession(heldCall.value.callId, heldCall.value.caller.number, heldCall.value.state)
			if (session) {
				globalEvents.emit('Call:Hangup', { sessionId: session.id })
			}
		} else if (dialingCall.value) { // NOSONAR
			const session = sipClient.getSession(dialingCall.value.callId, dialingCall.value.caller.number, dialingCall.value.state)
			if (session) {
				globalEvents.emit('Call:Hangup', { sessionId: session.id })
			}
		}
	} else if (ss.settings.phoneId) {
		if (dialingCall.value && heldCall.value) {
			try { api.get(`/pbx/app/action/hangup?target=${dialingCall.value._id}`) }
			catch (_) { globalEvents.emit('CommonErrorModal', { header: t('mycalls.header'), message: t('mycalls.failedToHangupCall') }) }
		} else if (activeCall.value) {
			try { api.get(`/pbx/app/action/hangup?target=${activeCall.value._id}`) }
			catch (_) { globalEvents.emit('CommonErrorModal', { header: t('mycalls.header'), message: t('mycalls.failedToHangupCall') }) }
		} else if (heldCall.value) {
			try { api.get(`/pbx/app/action/hangup?target=${heldCall.value._id}`) }
			catch (_) { globalEvents.emit('CommonErrorModal', { header: t('mycalls.header'), message: t('mycalls.failedToHangupCall') }) }
		} else if (dialingCall.value) { // NOSONAR
			try { api.get(`/pbx/app/action/hangup?target=${dialingCall.value._id}`) }
			catch (_) { globalEvents.emit('CommonErrorModal', { header: t('mycalls.header'), message: t('mycalls.failedToHangupCall') }) }
		}
	}
}

function bottomToggleHold() {
	if (ss.settings.softphoneId) {
		if (activeCall.value) {
			const session = sipClient.getSession(activeCall.value.callId, activeCall.value.caller.number, activeCall.value.state)
			if (session) {
				globalEvents.emit('Call:ActivateHold', { sessionId: session.id })
			}
		} else if (heldCall.value) {
			const session = sipClient.getSession(heldCall.value.callId, heldCall.value.caller.number, heldCall.value.state)
			if (session) {
				globalEvents.emit('Call:DeactivateHoldInternal', { sessionId: session.id })
			}
		}
	} else if (ss.settings.phoneId) {
		if (activeCall.value) {
			console.log(`Triggering event Call:ActivateHold for call ${activeCall.value.sessionId}`) // eslint-disable-line no-console
			globalEvents.emit('Call:ActivateHold', activeCall.value)
		} else if (heldCall.value) {
			globalEvents.emit('Call:DeactivateHold', heldCall.value)
		}
	}
}

function bottomSwap() {
	if (twoCalls.value && activeCall.value && heldCall.value) {
		if (ss.settings.softphoneId) {
			const session = sipClient.getSession(heldCall.value.callId, heldCall.value.caller.number, heldCall.value.state)
			if (session) {
				globalEvents.emit('Call:DeactivateHoldInternal', { sessionId: session.id })
			}
		} else if (ss.settings.phoneId) {
			globalEvents.emit('Call:DeactivateHold', heldCall.value)
		}
	}
}

function bottomTransfer() {
	if (twoCalls.value && activeCall.value && heldCall.value) {
		if (ss.settings.softphoneId || ss.settings.phoneId) {
			globalEvents.emit('Call:Transfer', { originator: activeCall.value, receiver: heldCall.value })
		}
	}
}

async function bottomTransferToDialing() {
	if (twoCalls.value && dialingCall.value && heldCall.value) {
		if (ss.settings.softphoneId) {
			const target = dialingCall.value.callee.number
			const source = heldCall.value

			// First we hangup the dialing call
			const session = sipClient.getSession(dialingCall.value.callId, dialingCall.value.caller.number, dialingCall.value.state)
			if (session) {
				globalEvents.emit('Call:Reject', { sessionId: session.id })
			}

			// Then we resume the held call to end music on hold
			const heldSession = sipClient.getSession(heldCall.value.callId, heldCall.value.caller.number, heldCall.value.state)
			if (heldSession) {
				globalEvents.emit('Call:DeactivateHoldInternal', { sessionId: heldSession.id })
			}

			await sleep(1000)

			// Then we make a blind transfer
			globalEvents.emit('BlindTransferRequested', { source: source._id, target: target })

			logger.info(`MyCalls: User requested blind transfer of call ${source._id} to ${target}`)
			api.get(`/pbx/app/action/transfer?source=${source._id}&target=${target}`).catch((err: Error) => {
				logger.error(`MyCalls: Error occurred while transferring call: ${err.message}`)
				globalEvents.emit('CommonErrorModal', { header: t('callDrag.transferFailedHeader'), message: t('callDrag.blindTransferError').replace('%%message%%', t('callDrag.transferTheCallToTheEmployee')) })
			})
		} else if (ss.settings.phoneId) {
			const target = dialingCall.value.callee.number
			const source = heldCall.value

			// First we hangup the dialing call
			try { await api.get(`/pbx/app/action/hangup?target=${dialingCall.value._id}`) }
			catch (err) {
				logger.error(`MyCalls: Error occurred while hanging up dialing call: ${(err as Error).message}`)
				globalEvents.emit('CommonErrorModal', { header: t('callDrag.transferFailedHeader'), message: t('callDrag.blindTransferError').replace('%%message%%', t('callDrag.transferTheCallToTheEmployee')) })
				return
			}

			// Then we resume the held call to end music on hold
			globalEvents.emit('Call:DeactivateHold', heldCall.value)

			await sleep(1000)

			// Then we make a blind transfer
			globalEvents.emit('BlindTransferRequested', { source: source._id, target: target })

			logger.info(`MyCalls: User requested blind transfer of call ${source._id} to ${target}`)
			api.get(`/pbx/app/action/transfer?source=${source._id}&target=${target}`).catch((err: Error) => {
				logger.error(`MyCalls: Error occurred while transferring call: ${err.message}`)
				globalEvents.emit('CommonErrorModal', { header: t('callDrag.transferFailedHeader'), message: t('callDrag.blindTransferError').replace('%%message%%', t('callDrag.transferTheCallToTheEmployee')) })
			})
		}
	}
}

// #endregion

initWatchers()
</script>

<style scoped>
	.view-box {
		overflow: auto;
	}

	/* ENTER */
	.list-enter-from {
		opacity: 0;
		transform: translateY(15px);
	}
	.list-enter-active {
		transition: opacity 0.3s ease, transform 0.3s ease;
	}
	.list-enter-to {
		opacity: 1;
		transform: translateY(0);
	}

	/* LEAVE */
	.list-leave-from {
		opacity: 1;
		transform: translateY(0);
	}
	.list-leave-active {
		transition: opacity 0.3s ease, transform 0.3s ease;
	}
	.list-leave-to {
		opacity: 0;
		transform: translateY(15px);
	}

	.dtmf-box {
		max-height: 0px;
		transition: 500ms;
		overflow: hidden;
		background: var(--p-primary-background);
		display: grid;
		grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
		column-gap: 2px;
		padding: 0px 2px 0px 2px;
	}
	.dtmf-box.show {
		max-height: 40px;
		box-shadow: var(--box-shadow-2);
		padding: 2px 2px 2px 2px;
	}
	.dtmf-button {
		cursor: pointer;
		background-color: var(--bg-color-dashboard-header);
		color: var(--txt-color-dashboard-header);
		text-align: center;
		padding: 3px;
		font-weight: 600;
		font-size: var(--font-size-primary);
		transition: background-color 1000ms, color 1000ms, transform 1000ms;
		border-radius: 5px 5px;
	}
	.dtmf-button:not(:first-child) {
		border-left: var(--divider-line-2);
	}
	.dtmf-button:hover {
		color: var(--button-background-primary);
		background-color: var(--p-primary-background);
		transition: background-color 200ms, color 200ms;
	}
	.dtmf-button:active {
		transform: translate(0,2px);
		transition: 100ms;
	}
	.recent-calls-box {
		max-height: 0px;
		transition: 500ms;
		overflow: hidden;
		background: var(--p-primary-background);
		margin-bottom: 5px;
	}
	.recent-calls-box.show {
		max-height: 300px;
		box-shadow: var(--box-shadow-2);
	}
	.recent-call-item {
		padding-left: 10px;
		padding-right: 10px;
		padding-top: 2px;
		padding-bottom: 2px;
		display: flex;
		align-items: center;
		cursor: pointer;

	}
	.recent-call-item:nth-of-type(odd) {
		background-color: #00000010;
	}
	.recent-call-item:not(:last-of-type) {
		border-bottom: 1px solid var(--p-select-border-color);
	}
	.recent-call-item:hover {
		background-color: var(--p-primary-hover-color);
		cursor: pointer;
	}
	.recent-call-item > .number {
		padding-left: 5px;
		color: var(--p-text-color);
		opacity: 0.8;
		font-size: 12px;
	}
	.recent-call-item > .number.noname {
		opacity: 1;
		font-size: inherit;
		font-weight: 600;
	}
	.recent-call-item:hover > .number {
		color: var(--p-primary-contrast-color);
	}
	.recent-call-item > .name {
		padding-left: 5px;
		color: var(--p-text-color);
		font-weight: 600;
	}
	.recent-call-item:hover > .name {
		color: var(--p-primary-contrast-color);
	}
	.recent-call-item > .time {
		padding-left: 5px;
		font-size: 12px;
		width: 70px;
		text-align: right;
		color: var(--p-text-muted-color);
		flex-shrink: 0;
	}

	.recent-call-item:hover > .time {
		color: var(--p-primary-contrast-color);
	}

	.text-muted {
		opacity: 0.5;
	}

	@media(prefers-color-scheme: dark) {
		.personal-queue-calls {
			background: #00000080;
		}
	}

	.personal-queue-calls {
		border-radius: 3px;
		background: #00000020;
		margin-top: 1px;
		height: 20px;
		display: flex;
		gap: 2px;
		padding-left: 5px;
		padding-right: 5px;
		animation: personal-queue-calls-animation 5s ease-in-out;
		animation-iteration-count: infinite;
	}

	@keyframes personal-queue-calls-animation {
	0% {
		color: var(--txt-color-dashboard-header);
		background: #00000020;
	}
	25% {
		background: #FFFFFF;
	}
	50% {
		color: var(--queue-red);
		background: #FFFFFF;
	}
	75% {
		background: #00000020;
	}
	100% {
		color: var(--txt-color-dashboard-header);
		background: #00000020;
	}
}

.bottom-buttons {
	position: absolute;
	bottom: 0px;
	left: 0px;
	right: 0px;
	background-image: var(--dialog-header-background);
	border-bottom-left-radius: 5px;
	border-bottom-right-radius: 5px;
	opacity: 0;
	height: 37px;
	transition: opacity 1000ms ease;
}

.bottom-buttons.show {
	opacity: 1;
}

.bottom-buttons :deep(.p-button > span) {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
</style>
